// Free geocoding via Photon (https://photon.komoot.io) — OSM-based, no API key.
// Chosen over Nominatim because it is typo-tolerant and indexes natural features
// (peaks, mountain passes, arêtes) well — e.g. it finds "Forcella Marcuoira"
// from a misspelled "Forcella Marcoira", which Nominatim missed entirely.
// Usage policy allows reasonable volume; keep it debounced (~1 req/keystroke-pause).
// Swap this module for Google/Mapbox if you later want richer place autocomplete.

export interface GeoResult {
  label: string;
  lat: number;
  lng: number;
  distanceKm?: number; // set only when a bias point was given (km from it)
  countrycode?: string; // ISO 3166-1 alpha-2 (uppercase) when Photon provides it
  country?: string; // localized country name when available
}

// Bias the search toward a point (e.g. a hike's start): the query is restricted
// to a bbox around it so far-away same-name places (and Photon's importance bias
// toward them) are excluded and the local POI surfaces. Distance from the point
// is attached to each result for display.
export interface GeoBias {
  lat: number;
  lng: number;
  maxKm?: number; // half-size of the search box, in km (default 50)
}

// Haversine distance in km.
const distanceKm = (aLat: number, aLng: number, bLat: number, bLng: number): number => {
  const R = 6371;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(bLat - aLat);
  const dLng = toRad(bLng - aLng);
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
};

// Build a human label from Photon's structured properties (it has no display_name).
// Start with the feature name, then append a deduped context trail.
const buildLabel = (p: any): string => {
  const parts: string[] = [];
  const push = (v: any) => {
    const s = v == null ? "" : String(v).trim();
    if (s && !parts.includes(s)) parts.push(s);
  };
  push(p.name);
  if (p.housenumber && p.street) push(`${p.street} ${p.housenumber}`);
  else push(p.street);
  push(p.city || p.county);
  push(p.state);
  push(p.country);
  return parts.join(", ");
};

export const geocode = async (
  query: string,
  signal?: AbortSignal,
  bias?: GeoBias
): Promise<GeoResult[]> => {
  // When biasing, ask for more candidates (importance-ranked) so the nearby one
  // isn't crowded out before we re-sort by distance.
  const params = new URLSearchParams({ q: query, limit: bias ? "15" : "6" });
  if (bias) {
    // Restrict to a box around the bias point. Photon's location_bias alone is
    // too weak to beat importance/token ranking (a foreign POI name loses to
    // same-token places worldwide); a bbox excludes them so the local match
    // surfaces. Box derived from maxKm.
    const maxKm = bias.maxKm ?? 50;
    const dLat = maxKm / 111;
    const dLng = maxKm / (111 * Math.max(0.2, Math.cos((bias.lat * Math.PI) / 180)));
    params.set("bbox", [bias.lng - dLng, bias.lat - dLat, bias.lng + dLng, bias.lat + dLat].join(","));
    params.set("lat", String(bias.lat));
    params.set("lon", String(bias.lng));
    params.set("location_bias_scale", "0.8");
  }
  const url = "https://photon.komoot.io/api/?" + params.toString();
  const res = await fetch(url, { signal, headers: { Accept: "application/json" } });
  if (!res.ok) return [];
  const data = await res.json();
  const features = data && Array.isArray(data.features) ? data.features : [];

  const seen = new Set<string>();
  let out: GeoResult[] = [];
  for (const f of features) {
    const coords = f && f.geometry && f.geometry.coordinates;
    if (!Array.isArray(coords) || coords.length < 2) continue;
    const lng = parseFloat(coords[0]);
    const lat = parseFloat(coords[1]);
    if (isNaN(lat) || isNaN(lng)) continue;
    const props = f.properties || {};
    const label = buildLabel(props);
    if (!label) continue;
    // Photon can return near-duplicate nodes for the same place; dedupe by label
    // plus coarse coordinates (~100 m).
    const key = label + "|" + lat.toFixed(3) + "|" + lng.toFixed(3);
    if (seen.has(key)) continue;
    seen.add(key);
    const r: GeoResult = { label, lat, lng };
    if (props.countrycode) r.countrycode = String(props.countrycode).toUpperCase();
    if (props.country) r.country = String(props.country);
    if (bias) r.distanceKm = distanceKm(bias.lat, bias.lng, lat, lng);
    out.push(r);
  }

  // When biased we keep Photon's own relevance order (it ranks the actual
  // name match first, e.g. the lake over a nearby "Villa <name>"); the bbox
  // already constrained geography and the distance labels show feasibility.
  if (bias) out = out.slice(0, 8);
  return out;
};
